/**
 * TekCreative Core JavaScript
 *
 * Essential code loaded on every page via the TC namespace.
 *
 * Controllers:
 * - TC.Header        - Sticky header behavior
 * - TC.MobileMenu    - Mobile navigation with multi-level submenus
 * - TC.Sidebar       - Sidebar panel toggle [data-sidebar-trigger]
 * - TC.MiniCart      - Mini cart panel [data-modal-mini-cart] + AJAX add/update/remove
 * - TC.Search        - Search popup toggle [data-search-trigger]
 * - TC.VideoPlayer   - Custom video players with thumbnails/progress
 * - TC.Accordion     - Reusable accordion functionality
 * - TC.LazyLoad      - Lazy loading fallback for older browsers
 * - TC.ScrollToTop   - Scroll-to-top buttons [data-scroll-top]
 * - TC.Ajax          - AJAX helper with WordPress nonce support
 * - TC.Utils         - Utility functions (debounce, throttle, etc.)
 *
 * All controllers auto-initialize on DOMContentLoaded.
 * Vendor libraries (AOS, carousels) are loaded separately in script.js.
 *
 * @package
 * @since 2.0.0
 */

// Namespace for global access
window.TC = window.TC || {};

import '../components/io.js';

/**
 * Header Controller
 * Handles sticky header behavior
 */
TC.Header = {
	init() {
		const fixedHeader = document.getElementById('header-fixed');
		if (!fixedHeader) {
			return;
		}

		const sticky = fixedHeader.offsetTop;
		const adminBar = document.querySelector('.admin-bar');
		const mobileMenu = document.querySelector('.mobile-menu');

		// Hide simple logo initially

		window.addEventListener(
			'scroll',
			() => {
				if (window.pageYOffset > sticky) {
					fixedHeader.classList.add('sticky');
					if (adminBar) {
						adminBar.classList.add('sticky-header');
					}
					if (mobileMenu) {
						mobileMenu.classList.add('sticky');
					}
				} else {
					fixedHeader.classList.remove('sticky');
					if (adminBar) {
						adminBar.classList.remove('sticky-header');
					}
					if (mobileMenu) {
						mobileMenu.classList.remove('sticky');
					}
				}
			},
			{ passive: true }
		);
	},
};

/**
 * Mobile Menu Controller
 * Handles mobile navigation with multi-level submenus
 */
TC.MobileMenu = {
	init() {
		this.burgerMenu = document.getElementById('mobilemenuicon');
		this.burgerMenuSecond = document.getElementById('mobilemenuiconsecond');
		this.mobileMenu = document.getElementById('mobilemenu');
		this.mobileMenuSubmenu = document.querySelectorAll(
			'.mobile-menu__submenu'
		);
		this.closeMenu = document.getElementById('mobile_close');
		this.body = document.querySelector('body');

		this.setupToggleButtons();
		this.setupCloseButton();
		this.setupMenuItems();
		this.setupDepth2Submenus();
		this.setupDepth3Submenus();
	},

	/**
	 * Create a menu item element (link)
	 * @param title
	 * @param url
	 * @param classes
	 */
	createMenuItem(title, url, classes) {
		const a = document.createElement('a');
		a.setAttribute('href', url);
		a.classList.add(classes);
		a.textContent = title;
		if (title) {
			a.setAttribute('aria-label', title);
		}
		return a;
	},

	/**
	 * Create a submenu button element
	 * @param ariaLabel
	 * @param classes
	 * @param textContent
	 */
	createSubmenuButton(ariaLabel, classes, textContent = '') {
		const button = document.createElement('button');
		button.setAttribute('type', 'button');
		button.classList.add(classes);
		button.setAttribute('aria-label', ariaLabel);
		button.setAttribute('aria-expanded', 'false');
		if (textContent) {
			button.textContent = textContent;
		}
		return button;
	},

	/**
	 * Setup burger menu toggle buttons
	 */
	setupToggleButtons() {
		if (this.burgerMenuSecond) {
			this.burgerMenuSecond.addEventListener('click', (event) => {
				event.preventDefault();
				this.burgerMenu?.click();
			});
		}

		if (this.burgerMenu) {
			this.burgerMenu.addEventListener('click', (event) => {
				event.preventDefault();
				this.mobileMenu?.classList.add('is-active');
				this.body?.classList.add('no-scroll');
			});
		}
	},

	/**
	 * Setup close button functionality
	 */
	setupCloseButton() {
		if (this.mobileMenu && this.closeMenu) {
			this.closeMenu.addEventListener('click', (event) => {
				event.preventDefault();
				this.mobileMenu.classList.remove('is-active');

				this.mobileMenuSubmenu.forEach((submenu) => {
					submenu.classList.remove('active');
					const mobileButtonsOpen = document.querySelectorAll(
						'.mobile-menu__open-submenu-3'
					);
					mobileButtonsOpen.forEach((button) => {
						button.classList.remove('active');
					});
				});

				this.body?.classList.remove('no-scroll');
			});
		}

		// Close on escape key
		document.addEventListener('keydown', (e) => {
			if (
				e.key === 'Escape' &&
				this.mobileMenu?.classList.contains('is-active')
			) {
				this.closeMenu?.click();
			}
		});
	},

	/**
	 * Prepend button row to submenu
	 * @param parentLink
	 * @param parentLabel
	 * @param child
	 * @param extraLink
	 * @param extraLabel
	 */
	prependButton(parentLink, parentLabel, child, extraLink, extraLabel) {
		const div = document.createElement('div');
		div.classList.add('mobile-menu__row');

		const a = document.createElement('a');
		a.classList.add('mobile-menu__submenu-2-title');
		a.setAttribute('href', parentLink);
		a.textContent = parentLabel;
		div.appendChild(a);

		if (extraLink) {
			const a2 = document.createElement('a');
			a2.classList.add('mobile-menu__submenu-2-button');
			a2.setAttribute('href', extraLink);
			a2.textContent = extraLabel;
			div.appendChild(a2);
		}

		child.prepend(div);
	},

	/**
	 * Add click event to arrow for submenu toggle
	 * @param arrowClass
	 * @param child
	 */
	addClickEventToArrow(arrowClass, child) {
		arrowClass.addEventListener('click', (event) => {
			event.preventDefault();
			if (event.target !== event.currentTarget) {
				return;
			}
			child.classList.toggle('active');
			arrowClass.classList.toggle('active');
		});
	},

	/**
	 * Close submenu handler
	 * @param closeButtons
	 */
	closeSubmenu(closeButtons) {
		const submenues = document.querySelectorAll('div.mobile-menu__submenu');

		closeButtons.forEach((close) => {
			close.addEventListener('click', (event) => {
				event.preventDefault();

				submenues.forEach((submenu) => {
					submenu.classList.remove('active');
				});

				const allChildArrows = document.querySelectorAll(
					'.mobile-menu__open-submenu--depth-2'
				);
				allChildArrows.forEach((childArrow) => {
					childArrow.classList.remove('active');
				});
			});
		});
	},

	/**
	 * Setup depth 1 menu items - accordion plus/X toggle
	 * The main mobile menu slide-in is handled by setupToggleButtons.
	 * Here we only change how depth-1 submenus open: in-place accordion.
	 */
	setupMenuItems() {
		const mobileMenu = document.getElementById('mobilemenu');
		if (!mobileMenu) {
			return;
		}

		// Only target direct depth-0 items with children in the menu wrap
		const parentItems = mobileMenu.querySelectorAll(
			'.mobile-menu__menu-wrap > .menu-item-has-children'
		);

		parentItems.forEach((parent) => {
			const link = parent.querySelector(':scope > .mobile-menu__link, :scope > a');
			const submenu = parent.querySelector(':scope > .mobile-menu__submenu');

			if (!link || !submenu) {
				return;
			}

			// Inject plus icon SVG at the start of the link if not present
			if (link && !link.querySelector('.mobile-menu__toggle-icon')) {
				const plusSvg =
					(window.tc_settings && window.tc_settings.plus_svg)
						? window.tc_settings.plus_svg
						: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="mobile-menu__toggle-icon"><path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor"/></svg>';
				link.insertAdjacentHTML('afterbegin', plusSvg);
			}

			link.addEventListener('click', (e) => {
				e.preventDefault();
				const isOpen = parent.classList.contains('is-open');

				if (isOpen) {
					parent.classList.remove('is-open');
					link.setAttribute('aria-expanded', 'false');
				} else {
					parent.classList.add('is-open');
					link.setAttribute('aria-expanded', 'true');
				}
			});
		});
	},

	/**
	 * Setup depth 2 menu items (nested submenus)
	 */
	setupDepth2Submenus() {
		const depth1Wrappers = document.querySelectorAll(
			'.mobile-menu__submenu--depth-1-wrapper'
		);
		if (!depth1Wrappers.length) {
			return;
		}

		depth1Wrappers.forEach((wrapper) => {
			// Get grandparent label (depth-0 menu item containing this wrapper)
			const grandParentItem = wrapper.parentElement;
			const grandParentLabel =
				grandParentItem
					?.querySelector(':scope > .mobile-menu__link')
					?.textContent?.trim() || '';

			const menuParents = wrapper.querySelectorAll(
				'.menu__submenu--depth-1 > .menu-item-has-children'
			);
			const backLabel =
				document
					.querySelector('.mobile-menu__menu')
					?.getAttribute('data-back') || 'Back';

			if (menuParents) {
				menuParents.forEach((parent) => {
					const parentLinkEl =
						parent.querySelector('.mobile-menu__link');
					if (!parentLinkEl) {
						return;
					}

					const parentLabel = parentLinkEl.textContent?.trim();
					const parentLink = parentLinkEl.getAttribute('href');
					const child = parent.querySelector(
						'div.mobile-menu__submenu'
					);

					if (!child) {
						return;
					}

					const extraLabel = parent.dataset.label;
					const extraLink = parent.dataset.link;

					// Build breadcrumb chain: "Grand Parent > Parent"
					const breadcrumbLabel = grandParentLabel
						? `${grandParentLabel} > ${parentLabel}`
						: parentLabel;

					// Add arrow button to parent
					parent.append(
						this.createSubmenuButton(
							`Open ${parentLabel} submenu`,
							'mobile-menu__open-submenu--depth-2'
						)
					);

					setTimeout(() => {
						// Add title row with breadcrumb and back button to submenu
						this.prependButton(
							parentLink,
							breadcrumbLabel,
							child,
							extraLink,
							extraLabel
						);
						child.prepend(
							this.createSubmenuButton(
								`${backLabel} to ${grandParentLabel || 'previous menu'}`,
								'mobile-menu__submenu-back--depth-2',
								backLabel
							)
						);

						// Setup close buttons for depth-2
						const closeSubmenuBtns = child.querySelectorAll(
							'.mobile-menu__submenu-back--depth-2'
						);
						this.closeDepth2Submenu(closeSubmenuBtns, child);

						// Setup open arrow
						const openSubmenuArrow = parent.querySelector(
							'.mobile-menu__open-submenu--depth-2'
						);
						this.addClickEventToArrow(openSubmenuArrow, child);

						// Make parent link also open submenu
						parentLinkEl.addEventListener('click', (e) => {
							e.preventDefault();
							openSubmenuArrow.click();
						});
					}, 150);
				});
			}
		});
	},

	/**
	 * Close depth-2 submenu handler
	 * @param closeButtons
	 * @param submenu
	 */
	closeDepth2Submenu(closeButtons, submenu) {
		closeButtons.forEach((close) => {
			close.addEventListener('click', (event) => {
				event.preventDefault();
				submenu.classList.remove('active');

				const arrow = submenu.parentElement?.querySelector(
					'.mobile-menu__open-submenu--depth-2'
				);
				if (arrow) {
					arrow.classList.remove('active');
				}
			});
		});
	},

	/**
	 * Setup depth 3 submenu toggles
	 */
	setupDepth3Submenus() {
		const openSubmenu3Elements = document.querySelectorAll(
			'.mobile-menu__open-submenu-3'
		);

		openSubmenu3Elements.forEach((element) => {
			element.addEventListener('click', function () {
				this.classList.toggle('active');

				const prevSibling = this.previousElementSibling;
				if (
					prevSibling &&
					prevSibling.classList.contains(
						'menu__submenu--depth-3-wrapper'
					)
				) {
					prevSibling.classList.toggle('active');
				}
			});
		});
	},
};

/**
 * Video Player Controller
 * Handles custom video players with thumbnails and progress bars
 */
TC.VideoPlayer = {
	init() {
		const containers = document.querySelectorAll('[data-video-container]');

		containers.forEach((container) => {
			this.initContainer(container);
		});
	},

	initContainer(container) {
		const video = container.querySelector('[data-video]');
		const playButton = container.querySelector('[data-play]');
		const smallPlayBtn = container.querySelector('[data-small-play]');
		const thumbnail = container.querySelector('[data-thumbnail]');
		const progressBar = container.querySelector('[data-video-progress]');
		const progressInner = container.querySelector(
			'[data-video-progress-inner]'
		);

		if (!video) {
			return;
		}

		let isScrubbing = false;

		// Update UI based on play/pause state
		const updateUI = () => {
			const isPlaying = !video.paused && !video.ended;

			if (smallPlayBtn) {
				smallPlayBtn.classList.toggle('playing', isPlaying);
				smallPlayBtn.classList.toggle('paused', !isPlaying);
			}

			if (thumbnail) {
				thumbnail.classList.toggle('inactive', isPlaying);
			}
		};

		// Play/pause toggle
		const togglePlay = () => {
			if (video.paused) {
				video.play();
			} else {
				video.pause();
			}
		};

		// Event listeners
		if (playButton) {
			playButton.addEventListener('click', togglePlay);
		}

		if (smallPlayBtn) {
			smallPlayBtn.addEventListener('click', togglePlay);
		}

		video.addEventListener('play', updateUI);
		video.addEventListener('pause', updateUI);
		video.addEventListener('ended', () => {
			if (thumbnail) {
				thumbnail.classList.remove('inactive');
			}
			video.currentTime = 0;
			updateUI();
		});

		// Progress bar
		if (progressBar && progressInner) {
			video.addEventListener('timeupdate', () => {
				if (!isScrubbing) {
					const percent = (video.currentTime / video.duration) * 100;
					progressInner.style.width = `${percent}%`;
				}
			});

			// Scrubbing
			const getPercent = (e) => {
				const rect = progressBar.getBoundingClientRect();
				const x = e.clientX - rect.left;
				return Math.min(Math.max(x / rect.width, 0), 1);
			};

			progressBar.addEventListener('mousedown', (e) => {
				isScrubbing = true;
				const percent = getPercent(e);
				video.currentTime = percent * video.duration;
				progressInner.style.width = `${percent * 100}%`;
			});

			document.addEventListener('mousemove', (e) => {
				if (!isScrubbing) {
					return;
				}
				const percent = getPercent(e);
				video.currentTime = percent * video.duration;
				progressInner.style.width = `${percent * 100}%`;
			});

			document.addEventListener('mouseup', () => {
				isScrubbing = false;
			});
		}
	},
};

/**
 * Accordion Controller
 * Reusable accordion functionality
 */
TC.Accordion = {
	init(selector = '[data-accordion]') {
		const accordions = document.querySelectorAll(selector);

		accordions.forEach((accordion) => {
			const items = accordion.querySelectorAll('[data-accordion-item]');

			items.forEach((item) => {
				const trigger = item.querySelector('[data-accordion-trigger]');
				const content = item.querySelector('[data-accordion-content]');

				if (!trigger || !content) {
					return;
				}

				trigger.addEventListener('click', () => {
					const isOpen = item.classList.contains('is-open');

					// Close all items in this accordion (optional: for single-open behavior)
					if (accordion.dataset.accordionSingle !== undefined) {
						items.forEach((i) => i.classList.remove('is-open'));
					}

					// Toggle current item
					item.classList.toggle('is-open', !isOpen);
				});
			});
		});
	},
};

/**
 * Lazy Load Images (fallback for browsers without native support)
 */
TC.LazyLoad = {
	init() {
		// Check for native lazy loading support
		if ('loading' in HTMLImageElement.prototype) {
			return; // Browser supports native lazy loading
		}

		// Fallback using IntersectionObserver
		const lazyImages = document.querySelectorAll('img[loading="lazy"]');

		if ('IntersectionObserver' in window) {
			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const img = entry.target;
						img.src = img.dataset.src || img.src;
						observer.unobserve(img);
					}
				});
			});

			lazyImages.forEach((img) => observer.observe(img));
		}
	},
};

/**
 * Sidebar Controller
 * Toggles .sidebar active class on [data-sidebar-trigger] click
 * Only initializes if sidebar is enabled in theme settings
 */
TC.Sidebar = {
	init() {
		// Check if sidebar is enabled in theme settings
		if (!window.tc_settings?.sidebar_enabled) {
			return;
		}

		const sidebar = document.querySelector('.sidebar');
		if (!sidebar) {
			return;
		}

		// Toggle on trigger click
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-sidebar-trigger]');
			if (trigger) {
				e.preventDefault();
				sidebar.classList.toggle('active');
				document.body.classList.toggle(
					'no-scroll',
					sidebar.classList.contains('active')
				);
			}
		});

		// Close on overlay click
		sidebar.addEventListener('click', (e) => {
			if (e.target === sidebar) {
				sidebar.classList.remove('active');
				document.body.classList.remove('no-scroll');
			}
		});

		// Close on Escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && sidebar.classList.contains('active')) {
				sidebar.classList.remove('active');
				document.body.classList.remove('no-scroll');
			}
		});
	},
};

/**
 * Mini Cart Controller
 *
 * Slide-in cart panel [data-modal-mini-cart], mechanically identical to
 * TC.Sidebar (outer overlay toggles "active", inner panel translates in).
 * Also owns the live AJAX add / update-quantity / remove-item behaviour and
 * keeps the header cart badge [data-mini-cart-count] in sync.
 *
 * Note: window.ajax_object is only guaranteed to exist once DOMContentLoaded
 * has fired (footer script order), so it is only ever read from inside
 * init()/event-handler methods below - never at module top-level.
 */
TC.MiniCart = {
	panel: null,

	init() {
		this.panel = document.querySelector('[data-modal-mini-cart]');
		if (!this.panel) {
			return;
		}

		this.bindOpenTriggers();
		this.bindCloseTriggers();
		this.bindQuantityChange();
		this.bindQuantityStepButtons();
		this.bindRemoveItem();
		this.bindAddToCart();
	},

	open() {
		this.panel.classList.add('active');
		document.body.classList.add('no-scroll');
	},

	close() {
		this.panel.classList.remove('active');
		document.body.classList.remove('no-scroll');
	},

	/**
	 * Header cart icon: open the panel when configured to, otherwise let the
	 * link's own href navigate to the cart page (progressive enhancement).
	 */
	bindOpenTriggers() {
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-mini-cart-trigger]');
			if (!trigger) {
				return;
			}

			if (trigger.dataset.cartAction === 'minicart') {
				e.preventDefault();
				this.open();
			}
		});
	},

	bindCloseTriggers() {
		// Close button and the "Continue Shopping" button both carry this attribute.
		document.addEventListener('click', (e) => {
			if (e.target.closest('[data-modal-mini-cart-close]')) {
				e.preventDefault();
				this.close();
			}
		});

		// Close on overlay click (click landing on the outer panel element itself).
		this.panel.addEventListener('click', (e) => {
			if (e.target === this.panel) {
				this.close();
			}
		});

		// Close on Escape key.
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.panel.classList.contains('active')) {
				this.close();
			}
		});
	},

	/**
	 * Quantity change on a mini-cart line item.
	 */
	bindQuantityChange() {
		document.addEventListener('change', (e) => {
			const input = e.target.closest('[data-mini-cart-qty]');
			if (!input) {
				return;
			}

			const quantity = parseInt(input.value, 10);

			this.request('tc_update_cart_item', {
				cart_item_key: input.dataset.miniCartQty,
				quantity: Number.isNaN(quantity) ? 0 : quantity,
			});
		});
	},

	/**
	 * +/- stepper buttons either side of the quantity input: adjust the
	 * input's value and fire a "change" event so bindQuantityChange()
	 * handles the AJAX update the same way it does for manual typing.
	 */
	bindQuantityStepButtons() {
		document.addEventListener('click', (e) => {
			const button = e.target.closest(
				'[data-mini-cart-qty-decrease], [data-mini-cart-qty-increase]'
			);
			if (!button) {
				return;
			}
			e.preventDefault();

			const wrap = button.closest('[data-mini-cart-qty-wrap]');
			const input = wrap
				? wrap.querySelector('[data-mini-cart-qty]')
				: null;
			if (!input) {
				return;
			}

			const step = parseInt(input.step, 10) || 1;
			const min = input.min !== '' ? parseInt(input.min, 10) : 0;
			const max = input.max !== '' ? parseInt(input.max, 10) : null;
			const current = parseInt(input.value, 10) || 0;
			const direction = button.hasAttribute(
				'data-mini-cart-qty-increase'
			)
				? 1
				: -1;

			let next = current + step * direction;
			next = Math.max(min, next);
			if (max !== null) {
				next = Math.min(max, next);
			}

			if (next === current) {
				return;
			}

			input.value = next;
			input.dispatchEvent(new Event('change', { bubbles: true }));
		});
	},

	/**
	 * Remove item from the mini cart.
	 */
	bindRemoveItem() {
		document.addEventListener('click', (e) => {
			const button = e.target.closest('[data-mini-cart-remove]');
			if (!button) {
				return;
			}
			e.preventDefault();

			this.request('tc_remove_cart_item', {
				cart_item_key: button.dataset.miniCartRemove,
			});
		});
	},

	/**
	 * Intercept WooCommerce's standard add-to-cart controls:
	 * - form.cart .single_add_to_cart_button on product pages
	 * - .add_to_cart_button.ajax_add_to_cart on archive/shop loops
	 */
	bindAddToCart() {
		document.addEventListener('click', (e) => {
			const singleButton = e.target.closest(
				'form.cart .single_add_to_cart_button'
			);
			if (singleButton) {
				const form = singleButton.closest('form.cart');
				if (!form) {
					return;
				}
				e.preventDefault();

				const formData = new FormData(form);

				// Simple product forms carry the product ID on the submit button's
				// value (name="add-to-cart"), not as a named field FormData(form)
				// can see. Variable product forms carry it as data-product_id.
				const productId =
					form.dataset.product_id || singleButton.value;
				if (productId) {
					formData.set('product_id', productId);
				}
				if (!formData.get('quantity')) {
					formData.set('quantity', '1');
				}

				this.addToCart(formData);
				return;
			}

			const loopButton = e.target.closest(
				'.add_to_cart_button.ajax_add_to_cart'
			);
			if (loopButton) {
				e.preventDefault();

				this.addToCart({
					product_id: loopButton.dataset.product_id,
					quantity: loopButton.dataset.quantity || 1,
				});
			}
		});
	},

	addToCart(payload) {
		const formData =
			payload instanceof FormData ? payload : this.toFormData(payload);

		this.postFormData('tc_add_to_cart', formData).then((response) => {
			if (!response || !response.success) {
				return;
			}

			this.updateFromResponse(response.data);

			if (response.data && response.data.open_on_add) {
				this.open();
			}
		});
	},

	/**
	 * Update-quantity / remove-item both share this request + swap flow.
	 * @param action
	 * @param data
	 */
	request(action, data) {
		return this.postFormData(action, this.toFormData(data)).then(
			(response) => {
				if (response && response.success) {
					this.updateFromResponse(response.data);
				}
				return response;
			}
		);
	},

	/**
	 * Swap in a fresh AJAX response: header badge + items/totals fragments.
	 * @param data
	 */
	updateFromResponse(data) {
		if (!data) {
			return;
		}

		if (this.panel && typeof data.items_html === 'string') {
			const itemsWrap = this.panel.querySelector(
				'[data-mini-cart-items]'
			);
			if (itemsWrap) {
				itemsWrap.innerHTML = data.items_html;
			}
		}

		if (this.panel && typeof data.totals_html === 'string') {
			const totalsWrap = this.panel.querySelector(
				'[data-mini-cart-totals]'
			);
			if (totalsWrap) {
				totalsWrap.innerHTML = data.totals_html;
			}
		}

		if (this.panel) {
			const footer = this.panel.querySelector('[data-mini-cart-footer]');
			if (footer) {
				footer.classList.toggle(
					'modal-mini-cart__footer--empty',
					!!data.is_empty
				);
			}
		}

		const count = data.count || 0;
		document.querySelectorAll('[data-mini-cart-count]').forEach((badge) => {
			badge.textContent = count;
			badge.classList.toggle('header__cart-count--hidden', count === 0);
		});
	},

	toFormData(data) {
		const formData = new FormData();
		Object.entries(data || {}).forEach(([key, value]) => {
			if (value !== undefined && value !== null) {
				formData.append(key, value);
			}
		});
		return formData;
	},

	postFormData(action, formData) {
		formData.append('action', action);
		formData.append('nonce', window.ajax_object?.nonce || '');

		const url = window.ajax_object?.ajaxurl || '/wp-admin/admin-ajax.php';

		return fetch(url, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());
	},
};

/**
 * Search Controller
 * Toggles .search-popup-001 active class on [data-search-trigger] click
 */
TC.Search = {
	init() {
		const searchPopup = document.querySelector('.search-popup-001');
		if (!searchPopup) {
			return;
		}

		// Open on trigger click
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-search-trigger]');
			if (trigger) {
				e.preventDefault();
				searchPopup.classList.add('active');
				document.body.classList.add('no-scroll');
				const searchInput =
					searchPopup.querySelector('input[type="text"]');
				if (searchInput) {
					setTimeout(() => searchInput.focus(), 100);
				}
			}
		});

		// Close on data-active-trigger click (close button)
		searchPopup.addEventListener('click', (e) => {
			const closeTrigger = e.target.closest('[data-active-trigger]');
			if (closeTrigger) {
				e.preventDefault();
				searchPopup.classList.remove('active');
				document.body.classList.remove('no-scroll');
			}
		});

		// Close on Escape key
		document.addEventListener('keydown', (e) => {
			if (
				e.key === 'Escape' &&
				searchPopup.classList.contains('active')
			) {
				searchPopup.classList.remove('active');
				document.body.classList.remove('no-scroll');
			}
		});
	},
};

/**
 * Scroll To Top Controller
 * Handles scroll-to-top buttons with [data-scroll-top] attribute
 */
TC.ScrollToTop = {
	init() {
		const scrollBtns = document.querySelectorAll('[data-scroll-top]');
		if (!scrollBtns.length) {
			return;
		}

		const toggleVisibility = () => {
			const shouldShow = (window.pageYOffset || document.documentElement.scrollTop) > 300;
			scrollBtns.forEach((btn) => {
				btn.classList.toggle('is-visible', shouldShow);
				btn.classList.toggle('active', shouldShow);
			});
		};

		window.addEventListener('scroll', toggleVisibility, { passive: true });
		toggleVisibility();

		scrollBtns.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				window.scrollTo({
					top: 0,
					behavior: 'smooth',
				});
			});
		});
	},
};

/**
 * AJAX Configuration
 * Makes ajax_object available globally
 */
TC.Ajax = {
	url: window.ajax_object?.ajaxurl || '/wp-admin/admin-ajax.php',
	nonce: window.ajax_object?.nonce || '',

	post(action, data = {}) {
		const formData = new FormData();
		formData.append('action', action);
		formData.append('nonce', this.nonce);

		Object.entries(data).forEach(([key, value]) => {
			formData.append(key, value);
		});

		return fetch(this.url, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());
	},
};

/**
 * Utility Functions
 */
TC.Utils = {
	// Debounce function
	debounce(func, wait = 100) {
		let timeout;
		return function executedFunction(...args) {
			const later = () => {
				clearTimeout(timeout);
				func(...args);
			};
			clearTimeout(timeout);
			timeout = setTimeout(later, wait);
		};
	},

	// Throttle function
	throttle(func, limit = 100) {
		let inThrottle;
		return function executedFunction(...args) {
			if (!inThrottle) {
				func(...args);
				inThrottle = true;
				setTimeout(() => (inThrottle = false), limit);
			}
		};
	},

	// Check if element is in viewport
	isInViewport(el) {
		const rect = el.getBoundingClientRect();
		return (
			rect.top >= 0 &&
			rect.left >= 0 &&
			rect.bottom <=
				(window.innerHeight || document.documentElement.clientHeight) &&
			rect.right <=
				(window.innerWidth || document.documentElement.clientWidth)
		);
	},

	// Remove query parameters from URL
	// Strips everything after "?" and updates browser history without page reload
	clearUrlParams() {
		const url = window.location.href.split('?')[0];
		window.history.replaceState(null, null, url);
	},
};

/**
 * Modal Controller
 * Reusable modal component with display modes and cookie controls
 *
 * Display modes:
 * - 'click': Opens on trigger element click (default)
 * - 'pageload': Opens automatically after delay
 * - 'exit-intent': Opens when user moves cursor to leave page
 *
 * Cookie types:
 * - 'session': Cookie expires when browser closes
 * - 'daily': Cookie expires after 24 hours
 * - 'once': Cookie expires after 365 days (show once ever)
 * - 'custom': Cookie expires after specified days
 */
TC.Modal = {
	modals: new Map(),

	init() {
		const modalElements = document.querySelectorAll('[data-modal-001]');

		modalElements.forEach((modal) => {
			const id = modal.dataset.modalId;
			if (!id) {
				return;
			}

			// Store modal reference
			this.modals.set(id, modal);

			// Initialize based on display mode
			const displayMode = modal.dataset.displayMode || 'click';
			const cookieEnabled = modal.dataset.cookieEnabled === 'true';

			// Check cookie before initializing
			if (cookieEnabled && this.hasCookie(modal)) {
				return; // Don't show modal if cookie exists
			}

			switch (displayMode) {
				case 'pageload':
					this.initPageload(modal);
					break;
				case 'exit-intent':
					this.initExitIntent(modal);
					break;
				case 'click':
				default:
					this.initClick(modal);
					break;
			}

			// Setup close handlers
			this.setupCloseHandlers(modal);
		});
	},

	/**
	 * Initialize click trigger mode
	 * @param modal
	 */
	initClick(modal) {
		const triggerSelector = modal.dataset.triggerSelector;
		if (!triggerSelector) {
			return;
		}

		document.addEventListener('click', (e) => {
			const trigger = e.target.closest(triggerSelector);
			if (trigger) {
				e.preventDefault();
				this.open(modal.dataset.modalId);
			}
		});
	},

	/**
	 * Initialize pageload mode with optional delay
	 * @param modal
	 */
	initPageload(modal) {
		const delay = parseInt(modal.dataset.displayDelay, 10) || 0;

		setTimeout(() => {
			this.open(modal.dataset.modalId);
		}, delay);
	},

	/**
	 * Initialize exit intent mode
	 * @param modal
	 */
	initExitIntent(modal) {
		let triggered = false;

		document.addEventListener('mouseout', (e) => {
			if (triggered) {
				return;
			}

			// Check if cursor left the viewport from top
			if (e.clientY < 0) {
				triggered = true;
				this.open(modal.dataset.modalId);
			}
		});
	},

	/**
	 * Setup close handlers (overlay, button, escape key)
	 * @param modal
	 */
	setupCloseHandlers(modal) {
		const closeOnOverlay = modal.dataset.closeOnOverlay === 'true';
		const closeOnEscape = modal.dataset.closeOnEscape === 'true';

		// Close button click
		const closeButtons = modal.querySelectorAll('[data-modal-001-close]');
		closeButtons.forEach((btn) => {
			btn.addEventListener('click', (e) => {
				e.preventDefault();
				this.close(modal.dataset.modalId);
			});
		});

		// Overlay click
		if (closeOnOverlay) {
			const overlay = modal.querySelector('[data-modal-001-overlay]');
			if (overlay) {
				overlay.addEventListener('click', () => {
					this.close(modal.dataset.modalId);
				});
			}
		}

		// Escape key
		if (closeOnEscape) {
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && modal.classList.contains('is-open')) {
					this.close(modal.dataset.modalId);
				}
			});
		}
	},

	/**
	 * Open modal by ID
	 * @param id
	 */
	open(id) {
		const modal =
			this.modals.get(id) ||
			document.querySelector(`#${id}[data-modal-001]`);
		if (!modal) {
			return;
		}

		modal.classList.add('is-open');
		modal.style.display = 'flex';
		modal.setAttribute('aria-hidden', 'false');
		document.body.classList.add('modal-001-open');

		// Focus trap: focus first focusable element
		const focusable = modal.querySelector(
			'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
		);
		if (focusable) {
			setTimeout(() => focusable.focus(), 100);
		}

		// Dispatch custom event
		modal.dispatchEvent(new CustomEvent('modal:open', { bubbles: true }));
	},

	/**
	 * Close modal by ID
	 * @param id
	 */
	close(id) {
		const modal =
			this.modals.get(id) ||
			document.querySelector(`#${id}[data-modal-001]`);
		if (!modal) {
			return;
		}

		modal.classList.remove('is-open');
		modal.setAttribute('aria-hidden', 'true');
		document.body.classList.remove('modal-001-open');

		// Set cookie if enabled
		const cookieEnabled = modal.dataset.cookieEnabled === 'true';
		if (cookieEnabled) {
			this.setCookie(modal);
		}

		// Hide after animation
		setTimeout(() => {
			if (!modal.classList.contains('is-open')) {
				modal.style.display = 'none';
			}
		}, 300);

		// Dispatch custom event
		modal.dispatchEvent(new CustomEvent('modal:close', { bubbles: true }));
	},

	/**
	 * Toggle modal by ID
	 * @param id
	 */
	toggle(id) {
		const modal =
			this.modals.get(id) ||
			document.querySelector(`#${id}[data-modal-001]`);
		if (!modal) {
			return;
		}

		if (modal.classList.contains('is-open')) {
			this.close(id);
		} else {
			this.open(id);
		}
	},

	/**
	 * Check if modal cookie exists
	 * @param modal
	 */
	hasCookie(modal) {
		const cookieName = modal.dataset.cookieName;
		if (!cookieName) {
			return false;
		}

		return document.cookie
			.split(';')
			.some((c) => c.trim().startsWith(`${cookieName}=`));
	},

	/**
	 * Set modal cookie based on type
	 * @param modal
	 */
	setCookie(modal) {
		const cookieName = modal.dataset.cookieName;
		const cookieType = modal.dataset.cookieType || 'session';
		const cookieExpiry = parseInt(modal.dataset.cookieExpiry, 10) || 1;

		if (!cookieName) {
			return;
		}

		let expires = '';

		switch (cookieType) {
			case 'session':
				// No expires = session cookie
				break;
			case 'daily':
				expires = this.getExpiryDate(1);
				break;
			case 'once':
				expires = this.getExpiryDate(365);
				break;
			case 'custom':
				expires = this.getExpiryDate(cookieExpiry);
				break;
		}

		const cookieString = `${cookieName}=1; path=/${expires ? `; expires=${expires}` : ''}`;
		document.cookie = cookieString;
	},

	/**
	 * Get cookie expiry date string
	 * @param days
	 */
	getExpiryDate(days) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		return date.toUTCString();
	},
};

/**
 * Initialize Core on DOM Ready
 */
document.addEventListener('DOMContentLoaded', () => {
	TC.Header.init();
	TC.MobileMenu.init();
	TC.Sidebar.init();
	TC.MiniCart.init();
	TC.Search.init();
	TC.VideoPlayer.init();
	TC.Accordion.init();
	TC.LazyLoad.init();
	TC.ScrollToTop.init();
	TC.Modal.init();
});

// Export for use in other modules
export default TC;
